import { useState } from 'react';
import { useDeleteCallbackMutation } from '../store/modules/merchant/api';
import { CallbackResponse, ConfigResponse } from '../store/modules/merchant/types';
import { toast } from '../util/toast';
import { useConfigUpsert } from './useConfigUpsert';
import { useCallbackUpsert } from './useCallbackUpsert';
import { isCallbackInheritedForMerchant } from '../util/configInheritance';

export type FulfillmentState = {
  isShippingEnabled: boolean;
  isOrderResolveEnabled: boolean;
  isOrderChangeEnabled: boolean;
  isOrderVerifyEnabled: boolean;
  callbackUrl: string;
};

type Props = {
  merchantId: string;
  originalFulfillmentState: FulfillmentState;
  noShippingRequiredConfig?: ConfigResponse;
  callbacks?: Array<CallbackResponse>;
};

const fulfillmentStateChanged = (original: FulfillmentState, updated: FulfillmentState): boolean =>
  (Object.keys(original) as Array<keyof FulfillmentState>).some(
    (key) => original[key] !== updated[key],
  );

type CallbackToggleUpdate = {
  callbackType: CallbackResponse['callbackType'];
  originalCallback?: CallbackResponse;
  wasEnabled: boolean;
  isEnabled: boolean;
};

const buildCallbackToggleUpdates = (
  original: FulfillmentState,
  updated: FulfillmentState,
  callbacks?: CallbackResponse[],
): CallbackToggleUpdate[] => [
  {
    callbackType: 'Products',
    originalCallback: callbacks?.find((c) => c.callbackType === 'Products'),
    wasEnabled: original.isOrderResolveEnabled,
    isEnabled: updated.isOrderResolveEnabled,
  },
  {
    callbackType: 'ShippingAndTax',
    originalCallback: callbacks?.find((c) => c.callbackType === 'ShippingAndTax'),
    wasEnabled: original.isOrderChangeEnabled,
    isEnabled: updated.isOrderChangeEnabled,
  },
  {
    callbackType: 'Fulfillability',
    originalCallback: callbacks?.find((c) => c.callbackType === 'Fulfillability'),
    wasEnabled: original.isOrderVerifyEnabled,
    isEnabled: updated.isOrderVerifyEnabled,
  },
];

const hasBlockedInheritedCallbackDisable = (
  merchantId: string,
  callbackUpdates: CallbackToggleUpdate[],
): boolean =>
  callbackUpdates.some(
    ({ originalCallback, wasEnabled, isEnabled }) =>
      wasEnabled && !isEnabled && isCallbackInheritedForMerchant(merchantId, originalCallback),
  );

export const useFullfillmentSubmit = ({
  merchantId,
  noShippingRequiredConfig,
  callbacks,
  originalFulfillmentState,
}: Props) => {
  const [deleteCallback] = useDeleteCallbackMutation();
  const [isLoading, setIsLoading] = useState(false);
  const { onConfigUpsert } = useConfigUpsert(merchantId);
  const { onCallbackUpsert } = useCallbackUpsert(merchantId);

  const submit = async (fulfillmentState: FulfillmentState): Promise<boolean> => {
    const original = originalFulfillmentState;
    const updated = fulfillmentState;
    const stateChanged = fulfillmentStateChanged(original, updated);
    const callbackUpdates = buildCallbackToggleUpdates(original, updated, callbacks);

    if (hasBlockedInheritedCallbackDisable(merchantId, callbackUpdates)) {
      throw new Error('FULFILLMENT_INHERITED_CALLBACK_CHANGES_BLOCKED');
    }

    let wroteConfig = false;
    if (original.isShippingEnabled !== updated.isShippingEnabled) {
      await onConfigUpsert({
        ...noShippingRequiredConfig,
        name: 'NO_SHIPPING_REQUIRED',
        value: !updated.isShippingEnabled,
        enabled: true,
      });
      wroteConfig = true;
    }

    const callbackPromises: Promise<unknown>[] = [];
    const mustUpdateUrl = original.callbackUrl !== updated.callbackUrl;

    callbackUpdates.forEach((update) => {
      const { callbackType, originalCallback, wasEnabled, isEnabled } = update;
      const inherited = isCallbackInheritedForMerchant(merchantId, originalCallback);

      if (!isEnabled) {
        if (!originalCallback || inherited) {
          return;
        }
        callbackPromises.push(
          deleteCallback({ callbackId: originalCallback.callbackId, merchantId }).unwrap(),
        );
        return;
      }

      if (!originalCallback) {
        callbackPromises.push(
          onCallbackUpsert({
            callbackType,
            url: updated.callbackUrl,
          }),
        );
        return;
      }

      if (inherited) {
        if (mustUpdateUrl || !wasEnabled) {
          callbackPromises.push(
            onCallbackUpsert({
              ...originalCallback,
              callbackType,
              url: updated.callbackUrl,
            }),
          );
        }
        return;
      }

      if (!wasEnabled || mustUpdateUrl) {
        callbackPromises.push(
          onCallbackUpsert({
            ...originalCallback,
            callbackType,
            url: updated.callbackUrl,
          }),
        );
      }
    });

    await Promise.all(callbackPromises);

    const wroteCallbacks = callbackPromises.length > 0;
    if (wroteConfig || wroteCallbacks) {
      return true;
    }

    if (stateChanged) {
      throw new Error('FULFILLMENT_INHERITED_CALLBACK_CHANGES_BLOCKED');
    }

    return false;
  };

  const submitFullfillment = async (fulfillmentState: FulfillmentState) => {
    setIsLoading(true);
    try {
      const saved = await submit(fulfillmentState);
      if (saved) {
        toast.success('Fulfillment settings saved successfully.');
      }
    } catch (err) {
      if (
        err instanceof Error &&
        err.message === 'FULFILLMENT_INHERITED_CALLBACK_CHANGES_BLOCKED'
      ) {
        toast.error(
          'These callback changes could not be saved. Inherited callbacks cannot be turned off from a child merchant yet.',
        );
        return;
      }
      toast.error('Something went wrong. Please try again later.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    submitFullfillment,
    isLoading,
  };
};
